import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { ProductCard } from '@/components/ProductCard';
import { Filter } from 'lucide-react';

const CATEGORIES = ['Brakes', 'Intake', 'Exhaust', 'Suspension', 'Tuning', 'Electronics', 'Cooling', 'Wheels & Tires', 'Maintenance'];

export default function Shop() {
    const [params, setParams] = useSearchParams();
    const [vehicles, setVehicles] = useState({});
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const make = params.get('make') || '';
    const model = params.get('model') || '';
    const year = params.get('year') || '';
    const trim = params.get('trim') || '';
    const category = params.get('category') || '';
    const part_type = params.get('part_type') || '';
    const search = params.get('search') || '';
    const vin_verified_only = params.get('vin_verified_only') === '1';

    useEffect(() => {
        api.get('/vehicles').then(({ data }) => setVehicles(data || {}));
    }, []);

    useEffect(() => {
        const qp = new URLSearchParams();
        if (make) qp.set('make', make);
        if (model) qp.set('model', model);
        if (year) qp.set('year', year);
        if (trim) qp.set('trim', trim);
        if (category) qp.set('category', category);
        if (part_type) qp.set('part_type', part_type);
        if (search) qp.set('search', search);
        if (vin_verified_only) qp.set('vin_verified_only', 'true');
        qp.set('limit', '100');
        setLoading(true);
        api.get(`/products?${qp.toString()}`)
            .then(({ data }) => {
                setItems(data.items || []);
                setTotal(data.total || 0);
            })
            .finally(() => setLoading(false));
    }, [make, model, year, trim, category, part_type, search, vin_verified_only]);

    const setParam = (k, v) => {
        const np = new URLSearchParams(params);
        if (v) np.set(k, v);
        else np.delete(k);
        // reset dependent fields
        if (k === 'make') {
            np.delete('model');
            np.delete('trim');
        }
        if (k === 'model') np.delete('trim');
        setParams(np);
    };

    const models = useMemo(() => (make && vehicles[make] ? Object.keys(vehicles[make]) : []), [make, vehicles]);
    const years = useMemo(() => (make && model && vehicles[make]?.[model] ? vehicles[make][model].years : []), [make, model, vehicles]);
    const trims = useMemo(() => (make && model && vehicles[make]?.[model] ? vehicles[make][model].trims : []), [make, model, vehicles]);

    const Filters = (
        <div className="space-y-5">
            <div>
                <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Search</div>
                <input
                    value={search}
                    onChange={(e) => setParam('search', e.target.value)}
                    placeholder="Brakes, K&N, SKU..."
                    data-testid="shop-search-input"
                    className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                />
            </div>
            <Select label="Make" value={make} onChange={(v) => setParam('make', v)} options={Object.keys(vehicles)} testid="shop-filter-make" />
            <Select label="Model" value={model} onChange={(v) => setParam('model', v)} options={models} testid="shop-filter-model" />
            <Select label="Year" value={year} onChange={(v) => setParam('year', v)} options={years.map(String)} testid="shop-filter-year" />
            <Select label="Trim" value={trim} onChange={(v) => setParam('trim', v)} options={trims} testid="shop-filter-trim" />
            <Select label="Category" value={category} onChange={(v) => setParam('category', v)} options={CATEGORIES} testid="shop-filter-category" />
            <Select label="Part Type" value={part_type} onChange={(v) => setParam('part_type', v)} options={['oem', 'upgrade']} testid="shop-filter-part-type" labels={{ oem: 'OEM', upgrade: 'Upgrade' }} />
            <label className="flex items-center gap-2 text-sm text-white/70">
                <input
                    type="checkbox"
                    checked={vin_verified_only}
                    data-testid="shop-filter-vin-only"
                    onChange={(e) => setParam('vin_verified_only', e.target.checked ? '1' : '')}
                    className="accent-amber-500"
                />
                VIN verified only
            </label>
            <button
                onClick={() => setParams({})}
                data-testid="shop-filter-reset"
                className="text-xs text-amber-300 hover:underline"
            >
                Reset filters
            </button>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <h1 className="font-display text-4xl md:text-5xl text-white">SHOP PARTS</h1>
                    <div className="text-sm text-white/50 mt-1">{loading ? 'Loading…' : `${items.length} of ${total} parts shown`}</div>
                </div>
                <button
                    className="lg:hidden px-3 py-2 text-sm rounded-md bg-white/5 border border-white/10 inline-flex items-center gap-1.5"
                    onClick={() => setDrawerOpen(true)}
                    data-testid="catalog-filters-open-button"
                >
                    <Filter className="w-4 h-4" /> Filters
                </button>
            </div>
            <div className="grid lg:grid-cols-[260px_1fr] gap-6">
                <aside className="hidden lg:block rrm-panel p-5 h-fit sticky top-20">{Filters}</aside>
                <div>
                    {items.length === 0 && !loading ? (
                        <div className="rrm-panel p-10 text-center text-white/60">No parts match these filters. Try resetting filters.</div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {items.map((p) => <ProductCard key={p.id} product={p} />)}
                        </div>
                    )}
                </div>
            </div>
            {drawerOpen && (
                <div className="fixed inset-0 z-50 bg-black/70 lg:hidden" onClick={() => setDrawerOpen(false)}>
                    <div className="absolute right-0 top-0 h-full w-[88%] max-w-sm rrm-panel p-5 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="font-display text-xl text-white">FILTERS</div>
                            <button className="text-white/60" onClick={() => setDrawerOpen(false)}>Close</button>
                        </div>
                        {Filters}
                    </div>
                </div>
            )}
        </div>
    );
}

function Select({ label, value, onChange, options, testid, labels = {} }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{label}</div>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
            >
                <option value="">All</option>
                {options.map((o) => (
                    <option key={o} value={o}>{labels[o] || o}</option>
                ))}
            </select>
        </div>
    );
}
